import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

import { formatCredits } from "../../constants/credits";
import {
  approvedFinalVideo,
  videoPreviewConcerns,
  videoPreviewStatus,
} from "../../hooks/useVideoPreview";
import { paidMediaSettlementMessage } from "../../utils/paidMediaSettlement";
import ConfirmSpendSheet from "../Credits/ConfirmSpendSheet";
import CreditsPaywallSheet from "../Credits/CreditsPaywallSheet";
import VideoClipDebugList from "./VideoClipDebugList";

const Card = styled.section`
  background: #ffffff;
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 16px;
  padding: 24px 26px;
  margin-bottom: 16px;

  @media (max-width: 767.98px) {
    padding: 20px 18px;
  }
`;

const Kicker = styled.p`
  margin: 0 0 8px;
  color: var(--brand-primary-700, #085552);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const Heading = styled.h2`
  margin: 0 0 6px;
  color: var(--brand-ink, #0b1c1e);
  font-size: 1.2rem;
`;

const Copy = styled.p`
  margin: 0;
  color: #4b5563;
  font-size: 0.92rem;
  line-height: 1.55;
`;

const SafetyNotice = styled.div`
  margin: 16px 0;
  padding: 13px 14px;
  border: 1px solid #f2cf79;
  border-radius: 10px;
  background: #fffbeb;
  color: #78350f;
  font-size: 0.86rem;
  line-height: 1.5;
`;

const ConcernsNotice = styled.div`
  margin: 16px 0 0;
  padding: 13px 14px;
  border: 1px solid #f2cf79;
  border-radius: 10px;
  background: #fffbeb;
  color: #78350f;
  font-size: 0.86rem;
  line-height: 1.5;

  ul {
    margin: 8px 0 0;
    padding-left: 20px;
  }

  ul ul {
    margin-top: 4px;
  }

  li + li {
    margin-top: 6px;
  }
`;

const VideoFrame = styled.div`
  margin-top: 18px;
  overflow: hidden;
  border-radius: 12px;
  background: #081415;

  video {
    display: block;
    width: 100%;
    max-height: 620px;
    background: #081415;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
`;

const Button = styled.button`
  min-height: 44px;
  padding: 0 17px;
  border: 1px solid
    ${(props) => (props.$primary ? "var(--brand-primary, #0e7e7a)" : "#cfd9db")};
  border-radius: 999px;
  background: ${(props) => (props.$primary ? "var(--brand-primary, #0e7e7a)" : "#ffffff")};
  color: ${(props) => (props.$primary ? "#ffffff" : "var(--brand-ink, #0b1c1e)")};
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${(props) => (props.$primary ? "#0b605d" : "#f3f4f6")};
  }

  &:disabled {
    opacity: 0.58;
    cursor: not-allowed;
  }
`;

const RejectButton = styled(Button)`
  border-color: #fecaca;
  color: #991b1b;
`;

const Select = styled.select`
  min-height: 44px;
  padding: 0 34px 0 12px;
  border: 1px solid #cfd9db;
  border-radius: 8px;
  background: #ffffff;
  color: #0b1c1e;
`;

const FieldLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #374151;
  font-size: 0.88rem;
  font-weight: 600;
`;

const Confirm = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 14px;
  color: #374151;
  font-size: 0.86rem;
  line-height: 1.45;

  input {
    width: 18px;
    height: 18px;
    margin-top: 1px;
    flex: 0 0 auto;
  }
`;

const Status = styled.div`
  margin-top: 16px;
  padding: 13px 14px;
  border-radius: 10px;
  background: #eef5f4;
  color: #155e5b;
  font-size: 0.88rem;
  line-height: 1.5;
`;

const spinnerRotation = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 15px;
  height: 15px;
  margin-right: 8px;
  vertical-align: -2px;
  border: 2px solid rgba(14, 126, 122, 0.25);
  border-top-color: var(--brand-primary, #0e7e7a);
  border-radius: 50%;
  animation: ${spinnerRotation} 0.9s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2.5s;
  }
`;

const ErrorText = styled.p`
  margin: 14px 0 0;
  color: #b91c1c;
  font-size: 0.86rem;
`;

const ReviewSummary = styled.p`
  margin: 14px 0 0;
  color: #4b5563;
  font-size: 0.86rem;
  line-height: 1.5;
`;

const DestinationNotice = styled.p`
  margin: 14px 0 0;
  padding: 12px 14px;
  border-left: 3px solid #94a3b8;
  background: #f8fafc;
  color: #374151;
  font-size: 0.84rem;
  line-height: 1.5;
`;

/** @param {unknown} value */
const boundedText = (value) =>
  typeof value === "string" && value.trim() ? value.trim().slice(0, 600) : "";

/** @param {Record<string, any> | null | undefined} preview */
const reviewSummaryText = (preview) => {
  const summary = preview?.review_summary;
  if (typeof summary === "string") return boundedText(summary);
  if (!summary || typeof summary !== "object") return "";
  return boundedText(summary.summary) || boundedText(summary.message);
};

/** @param {number | string | null | undefined} value */
const safeCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0;
};
/**
 * What a walkthrough costs: one price for the finished video, however many
 * clips it takes to make. Null when the listing is not metered (legacy) —
 * the card then shows no price anywhere.
 * @param {{cost?: number, cost_per_clip?: number, free_redo_available?: boolean} | null | undefined} credits
 * @param {number} clipCount
 */
const videoQuote = (credits, clipCount) => {
  const price = Number(credits?.cost ?? credits?.cost_per_clip);
  if (!credits || !Number.isFinite(price)) {
    return null;
  }
  const clips = Math.max(1, Math.floor(Number(clipCount) || 1));
  const free = credits.free_redo_available === true;
  return {
    clips,
    cost: free ? 0 : price,
    free,
    // The clip count still tells the agent how long the walkthrough will be;
    // it just no longer multiplies the price.
    label: `${clips} ${clips === 1 ? "clip" : "clips"} · ${free ? "your free redo" : formatCredits(price)}`,
  };
};

/**
 * @param {string} status
 * @param {boolean} [hasConcerns]
 * @param {string} [failedNote] Appended to the failed message (the refund fact).
 */
const statusCopy = (status, hasConcerns = false, failedNote = "") => {
  if (status === "failed" && failedNote) {
    return `The preview video could not be generated, so there is nothing to review. ${failedNote}`;
  }
  const messages = /** @type {Record<string, string>} */ ({
    queued: "Your private video draft is queued.",
    requested: "Your private video draft is queued.",
    selecting: "Choosing listing photos to use for the video draft.",
    generating: "Creating video clips from the selected listing photos.",
    reviewing: "Checking generated clips against the source listing photos.",
    stitching:
      "Combining generated clips into a private draft. Flagged or unverified clips remain included for your review.",
    processing: "Preparing your private video draft.",
    awaiting_review: hasConcerns
      ? "Your private draft is ready with flagged or unverified details. Your review is required."
      : "Your private draft is ready for your review.",
    approved: "Approved for sharing.",
    rejected: "This preview was rejected and will not be shared.",
    failed: "The preview video could not be generated, so there is nothing to review.",
    cancelled: "Generation was cancelled.",
  });
  return messages[status] || "Ready to create a walkthrough preview.";
};

/**
 * @param {((...args: any[]) => unknown) | null | undefined} callback
 * @param {...any} args
 */
const invokeSafely = (callback, ...args) => {
  if (!callback) return;
  Promise.resolve(callback(...args)).catch(() => {});
};

/**
 * @param {{concern: {ordinal: number | null, summary: string, details: string[], coverageNotes: string}}} props
 */
const ConcernItem = ({ concern }) => (
  <li>
    {concern.ordinal ? `Clip ${concern.ordinal}: ` : ""}
    {concern.summary}
    {concern.details.length > 0 && (
      <ul>
        {concern.details.map((detail) => (
          <li key={detail}>{detail}</li>
        ))}
      </ul>
    )}
    {concern.coverageNotes ? <div>{concern.coverageNotes}</div> : null}
  </li>
);

/**
 * @param {{
 *   variant?: "owner" | "public",
 *   compactWhenIdle?: boolean,
 *   enabled?: boolean,
 *   phase?: string,
 *   preview?: Record<string, any> | null,
 *   generationLimits?: {default_clip_count?: number, max_clip_count?: number, daily_clip_limit?: number} | null,
 *   error?: string,
 *   action?: string | null,
 *   externalBusy?: string | null,
 *   externalError?: string,
 *   playbackUrl?: string,
 *   playbackLoading?: boolean,
 *   playbackError?: string,
 *   pollTimedOut?: boolean,
 *   startPending?: boolean,
 *   startChecking?: boolean,
 *   startMessage?: string,
 *   startSettlement?: Record<string, any> | null,
 *   onCheckStartStatus?: () => Promise<unknown> | unknown,
 *   credits?: {cost: number, free_redo_available: boolean} | null,
 *   creditsBlock?: {balance: number, cost: number, action: string} | null,
 *   balance?: number | null,
 *   listingUuid?: string,
 *   onSpendOpen?: () => unknown,
 *   onDismissCreditsBlock?: () => unknown,
 *   onGenerate?: (clipCount: number) => Promise<unknown> | unknown,
 *   onApprove?: () => Promise<unknown> | unknown,
 *   onReject?: () => Promise<unknown> | unknown,
 *   onRetry?: () => Promise<unknown> | unknown,
 *   onDownload?: () => Promise<unknown> | unknown,
 *   onShare?: () => Promise<unknown> | unknown,
 * }} props
 *   credits: the owner's quote (pricing v2); balance: the account balance for
 *   the confirm line (null = unknown). creditsBlock: a generate 409ed on the
 *   balance — the paywall sheet opens in place. Owner variant only; the public
 *   variant renders none of it.
 */
const VideoPreviewCard = ({
  variant = "owner",
  compactWhenIdle = false,
  enabled = true,
  phase = "ready",
  preview = null,
  generationLimits = null,
  error = "",
  action = null,
  externalBusy = null,
  externalError = "",
  playbackUrl = "",
  playbackLoading = false,
  playbackError = "",
  pollTimedOut = false,
  startPending = false,
  startChecking = false,
  startMessage = "",
  startSettlement = null,
  onCheckStartStatus,
  credits = null,
  creditsBlock = null,
  balance = null,
  listingUuid = "",
  onSpendOpen,
  onDismissCreditsBlock,
  onGenerate,
  onApprove,
  onReject,
  onRetry,
  onDownload,
  onShare,
}) => {
  const configuredMax = Number(generationLimits?.max_clip_count);
  const configuredDefault = Number(generationLimits?.default_clip_count);
  const configuredDailyLimit = Number(generationLimits?.daily_clip_limit);
  const hasServerLimits =
    Number.isInteger(configuredMax) &&
    configuredMax >= 1 &&
    configuredMax <= 5 &&
    Number.isInteger(configuredDefault) &&
    configuredDefault >= 1 &&
    configuredDefault <= configuredMax &&
    Number.isInteger(configuredDailyLimit) &&
    configuredDailyLimit >= configuredMax &&
    configuredDailyLimit <= 125;
  const maxClipCount = hasServerLimits ? configuredMax : 1;
  const defaultClipCount = hasServerLimits ? configuredDefault : 1;
  const [clipCount, setClipCount] = useState(defaultClipCount);
  const [generationAcknowledged, setGenerationAcknowledged] = useState(false);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [spendConfirmOpen, setSpendConfirmOpen] = useState(false);
  const [idleExpanded, setIdleExpanded] = useState(false);
  const headingRef = useRef(/** @type {HTMLHeadingElement|null} */ (null));
  const status = videoPreviewStatus(preview);
  // Owner-only pricing (pricing v2). The public variant never sees a quote.
  const quote = variant === "owner" ? videoQuote(credits, clipCount) : null;
  // A failed preview is not proof of what this purchase charged or refunded.
  const failedNote =
    variant === "owner" && status === "failed" ? paidMediaSettlementMessage(startSettlement) : "";
  const finalVideo = preview?.is_stale === true ? null : preview?.final_video;
  const publicApprovedVideo = approvedFinalVideo(preview);
  const video = variant === "public" ? publicApprovedVideo : finalVideo;
  const artifactAvailable = video?.available === true && typeof video.play_url === "string";
  const playableUrl = variant === "public" ? video?.play_url || "" : playbackUrl;
  const videoAvailable = artifactAvailable && !!playableUrl;
  const busy = !!action || !!externalBusy;
  const isStale = preview?.is_stale === true;
  const summary = reviewSummaryText(preview);
  // Concerns are owner-only by contract; public/shared payloads never carry
  // them, and the public variant must never render them regardless.
  const concerns = variant === "owner" ? videoPreviewConcerns(preview) : [];
  const flaggedConcerns = concerns.filter((concern) => concern.kind === "flagged");
  const unverifiedConcerns = concerns.filter((concern) => concern.kind !== "flagged");
  const requested = safeCount(preview?.requested_clip_count);
  const completed = safeCount(preview?.completed_clip_count);
  // Flagged clips are still included in the draft; only clips that never
  // generated are excluded from it.
  const excluded = safeCount(preview?.excluded_clip_count);
  const active = [
    "queued",
    "requested",
    "selecting",
    "generating",
    "reviewing",
    "stitching",
    "processing",
  ].includes(status);
  const statusArtifactPending =
    ["awaiting_review", "approved"].includes(status) && !artifactAvailable;
  const canRetryStatus =
    variant === "owner" &&
    !startPending &&
    !!onRetry &&
    !!error &&
    !pollTimedOut &&
    (phase === "error" || active || statusArtifactPending);
  // The component and its form stay mounted. Only an idle, unstarted option
  // may be collapsed; current media or anything needing attention stays open.
  const showIdleDisclosure =
    compactWhenIdle &&
    variant === "owner" &&
    phase === "ready" &&
    (!preview || (status === "not_requested" && !preview.id && !preview.uuid)) &&
    !preview?.final_video &&
    !(Array.isArray(preview?.clips) && preview.clips.length) &&
    !summary &&
    !concerns.length &&
    !isStale &&
    !busy &&
    !error &&
    !externalError &&
    !playbackError &&
    !playbackLoading &&
    !pollTimedOut &&
    !startPending &&
    !startChecking &&
    !creditsBlock &&
    !spendConfirmOpen;
  useEffect(() => {
    setClipCount(Math.max(1, Math.min(maxClipCount, defaultClipCount)));
  }, [defaultClipCount, maxClipCount]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: confirmation is intentionally reset when the reviewed preview changes.
  useEffect(() => {
    setReviewConfirmed(false);
  }, [preview?.uuid, status]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: generation acknowledgement is scoped to one preview identity.
  useEffect(() => {
    setGenerationAcknowledged(false);
  }, [preview?.uuid]);

  if (
    (!enabled && !preview && phase !== "error" && !startPending) ||
    phase === "idle" ||
    phase === "loading" ||
    (phase === "disabled" && !startPending)
  )
    return null;
  if (variant === "public" && !publicApprovedVideo) return null;

  return (
    <Card
      aria-labelledby={
        showIdleDisclosure
          ? `video-preview-disclosure-${variant}`
          : `video-preview-heading-${variant}`
      }
    >
      {showIdleDisclosure && (
        <>
          <Button
            id={`video-preview-disclosure-${variant}`}
            type="button"
            aria-expanded={idleExpanded}
            aria-controls={`video-preview-content-${variant}`}
            onClick={() => setIdleExpanded((expanded) => !expanded)}
          >
            Optional listing video
          </Button>
          {!idleExpanded && (
            <Copy>
              {quote ? "See clip options and cost." : "See video options."} Opening this does not
              start generation.
            </Copy>
          )}
        </>
      )}
      <div id={`video-preview-content-${variant}`} hidden={showIdleDisclosure && !idleExpanded}>
        <Kicker>{variant === "public" ? "Listing walkthrough" : "Video preview"}</Kicker>
        <Heading
          ref={headingRef}
          id={`video-preview-heading-${variant}`}
          tabIndex={variant === "owner" ? -1 : undefined}
        >
          {variant === "public" ? "Walk through the property" : "Create a listing walkthrough"}
        </Heading>
        <Copy>
          {variant === "public"
            ? "A short AI-generated motion preview based on listing photos."
            : "Prepare a short, silent MP4 draft from listing photos. It stays private until you review and approve it for sharing."}
        </Copy>

        <SafetyNotice>
          <strong>
            {variant === "public" ? "AI-generated motion." : "Accuracy review required."}
          </strong>{" "}
          {variant === "public"
            ? "It is a visual preview, not a recording of a physical walkthrough."
            : "This walkthrough uses AI-generated motion from listing photos processed by Google's Gemini and Veo services. Automated checks help catch issues, but they cannot guarantee accuracy. Watch the complete video and verify every room, fixture, opening, view, and property feature against the original listing photos before approving or sharing it."}
        </SafetyNotice>

        {videoAvailable && (
          <VideoFrame>
            {/* biome-ignore lint/a11y/useMediaCaption: final walkthrough exports are silent and contain no audio track. */}
            <video
              key={playableUrl}
              src={playableUrl}
              controls
              playsInline
              preload="metadata"
              aria-label={
                variant === "public"
                  ? "AI-generated listing walkthrough"
                  : "AI-generated listing walkthrough preview"
              }
            >
              Your browser does not support embedded video. Use the download button instead.
            </video>
          </VideoFrame>
        )}

        {variant === "owner" && artifactAvailable && playbackLoading && (
          <Status as="output" aria-live="polite">
            Loading the private video for review…
          </Status>
        )}
        {variant === "owner" && playbackError && (
          <ErrorText role="alert">{playbackError}</ErrorText>
        )}

        {variant === "owner" && startPending && (
          <Status as="output" aria-live="polite">
            {startMessage ||
              "We haven’t confirmed this request’s outcome yet. Check its status before starting another video."}
            {onCheckStartStatus && (
              <Controls>
                <Button
                  type="button"
                  disabled={startChecking}
                  onClick={() => invokeSafely(onCheckStartStatus)}
                >
                  {startChecking ? "Checking status…" : "Check status"}
                </Button>
              </Controls>
            )}
          </Status>
        )}

        {variant === "owner" && isStale && (
          <Status as="output" aria-live="polite">
            This preview is out of date because the listing photos or package changed. It is blocked
            from sharing. Generate and review a new version.
            {quote &&
              (quote.free
                ? " A new version uses your free redo."
                : ` A new version is ${formatCredits(Number(credits?.cost))}.`)}
          </Status>
        )}

        {variant === "owner" && !isStale && preview && (
          <Status as="output" aria-live="polite">
            <strong>
              {active && <Spinner data-testid="video-preview-spinner" aria-hidden="true" />}
              {statusCopy(status, concerns.length > 0, failedNote)}
            </strong>
            {requested > 0 && status !== "failed" && (
              <div>
                {completed} of {requested} clips processed
                {excluded > 0 ? `; ${excluded} not included` : ""}.
              </div>
            )}
            {active && (
              <div>
                This card checks for updates automatically. You can leave this page and return to
                check progress; leaving does not cancel the request.
              </div>
            )}
          </Status>
        )}

        {summary && <ReviewSummary>Safety review: {summary}</ReviewSummary>}

        {variant === "owner" &&
          !startPending &&
          enabled &&
          (!preview ||
            isStale ||
            ["not_requested", "failed", "rejected", "cancelled"].includes(status)) && (
            <>
              {maxClipCount > 1 && (
                <Controls>
                  <FieldLabel htmlFor="video-preview-clip-count">
                    Clips
                    <Select
                      id="video-preview-clip-count"
                      value={clipCount}
                      disabled={busy}
                      onChange={(event) => setClipCount(Number(event.target.value))}
                    >
                      {Array.from({ length: maxClipCount }, (_, index) => index + 1).map(
                        (count) => (
                          <option key={count} value={count}>
                            {count}
                          </option>
                        )
                      )}
                    </Select>
                  </FieldLabel>
                </Controls>
              )}
              <Confirm htmlFor="video-preview-generation-ack">
                <input
                  id="video-preview-generation-ack"
                  type="checkbox"
                  checked={generationAcknowledged}
                  disabled={busy}
                  onChange={(event) => setGenerationAcknowledged(event.target.checked)}
                />
                <span>
                  I understand that I must watch the complete video and verify every property detail
                  before it can be shared.
                </span>
              </Confirm>
              <Controls>
                <Button
                  type="button"
                  $primary
                  disabled={busy || !generationAcknowledged}
                  onClick={() => {
                    if (quote) {
                      // The spend is confirmed as one fact first; the server's
                      // 409 stays the authority (creditsBlock below).
                      setSpendConfirmOpen(true);
                      if (onSpendOpen) invokeSafely(onSpendOpen);
                      return;
                    }
                    invokeSafely(onGenerate, clipCount);
                  }}
                >
                  {action === "generate"
                    ? "Starting…"
                    : quote
                      ? `Prepare · ${quote.label}`
                      : preview
                        ? "Generate a new preview"
                        : "Generate preview"}
                </Button>
              </Controls>
            </>
          )}

        {quote && spendConfirmOpen && !startPending && (
          <ConfirmSpendSheet
            title={`Prepare a walkthrough · ${quote.label}`}
            cost={quote.cost}
            balance={balance}
            note={
              quote.free
                ? "Your free redo covers this request. Generated clips may still have flagged or unverified details. Your review is required before sharing."
                : "Credits pay for clip generation. Generated clips with flagged or unverified details still count; failed clip generation is refunded automatically. Your review is required before sharing."
            }
            confirmLabel="Prepare"
            busy={busy}
            onConfirm={() => {
              setSpendConfirmOpen(false);
              invokeSafely(onGenerate, clipCount);
            }}
            onClose={() => {
              if (!busy) setSpendConfirmOpen(false);
            }}
          />
        )}

        {variant === "owner" && creditsBlock && (
          <CreditsPaywallSheet
            block={creditsBlock}
            // The confirmation's opener can disappear while generation starts.
            // A later rejected request still has this persistent neutral target.
            returnFocusRef={headingRef}
            onClose={() => {
              if (onDismissCreditsBlock) invokeSafely(onDismissCreditsBlock);
            }}
            listingUuid={listingUuid || undefined}
          />
        )}

        {variant === "owner" && status === "awaiting_review" && !isStale && (
          <>
            {concerns.length > 0 && (
              <ConcernsNotice data-testid="video-preview-concerns">
                {flaggedConcerns.length > 0 && (
                  <>
                    <strong>
                      Automated review flagged possible inaccuracies — check these against your
                      listing photos before approving:
                    </strong>
                    <ul>
                      {flaggedConcerns.map((concern, index) => (
                        <ConcernItem
                          key={concern.ordinal ?? `flagged-${index}`}
                          concern={concern}
                        />
                      ))}
                    </ul>
                  </>
                )}
                {unverifiedConcerns.length > 0 && (
                  <>
                    <strong>
                      We couldn’t verify these details — review them in the video before approving:
                    </strong>
                    <ul>
                      {unverifiedConcerns.map((concern, index) => (
                        <ConcernItem
                          key={concern.ordinal ?? `unverified-${index}`}
                          concern={concern}
                        />
                      ))}
                    </ul>
                  </>
                )}
              </ConcernsNotice>
            )}
            <DestinationNotice>
              Approving this video makes it visible immediately on every existing active share link
              for this report version. You can remove the video from sharing later without revoking
              those report links.
            </DestinationNotice>
            {videoAvailable && (
              <Confirm htmlFor="video-preview-review-confirmation">
                <input
                  id="video-preview-review-confirmation"
                  type="checkbox"
                  checked={reviewConfirmed}
                  disabled={busy}
                  onChange={(event) => setReviewConfirmed(event.target.checked)}
                />
                <span>
                  I watched the entire preview and verified it does not add, remove, or misrepresent
                  any property feature.
                </span>
              </Confirm>
            )}
            <Controls>
              {videoAvailable && (
                <Button
                  type="button"
                  $primary
                  disabled={busy || !reviewConfirmed}
                  onClick={() => invokeSafely(onApprove)}
                >
                  {action === "approve" ? "Approving…" : "Approve for sharing"}
                </Button>
              )}
              <RejectButton type="button" disabled={busy} onClick={() => invokeSafely(onReject)}>
                {action === "reject" ? "Rejecting…" : "Reject preview"}
              </RejectButton>
            </Controls>
          </>
        )}

        {variant === "owner" && status === "approved" && !isStale && (
          <>
            {artifactAvailable && (
              <DestinationNotice>
                Before downloading or sharing, you must verify seller, brokerage, MLS, and
                destination rules for AI-generated media. Listino does not certify this video as
                Zillow compliant or approved for any destination.
              </DestinationNotice>
            )}
            <Controls>
              {artifactAvailable && onDownload && (
                <Button
                  type="button"
                  $primary
                  disabled={busy}
                  onClick={() => invokeSafely(onDownload)}
                >
                  {externalBusy === "download" ? "Refreshing…" : "Download MP4"}
                </Button>
              )}
              {artifactAvailable && onShare && (
                <Button type="button" disabled={busy} onClick={() => invokeSafely(onShare)}>
                  {externalBusy === "share" ? "Preparing link…" : "Copy share link"}
                </Button>
              )}
              {onReject && (
                <RejectButton type="button" disabled={busy} onClick={() => invokeSafely(onReject)}>
                  {action === "reject" ? "Removing…" : "Remove from sharing"}
                </RejectButton>
              )}
            </Controls>
          </>
        )}

        {variant === "public" && onDownload && (
          <Controls>
            <Button type="button" $primary disabled={busy} onClick={() => invokeSafely(onDownload)}>
              {externalBusy === "download" ? "Refreshing…" : "Download walkthrough"}
            </Button>
          </Controls>
        )}

        {variant === "owner" && (
          <VideoClipDebugList
            clips={preview?.clips}
            cacheKeyBase={`${preview?.uuid || preview?.id || ""}:${preview?.document_version || 0}`}
          />
        )}

        {pollTimedOut && !startPending && (
          <Status as="output" aria-live="polite">
            Automatic status checks have paused. Refresh to check the latest outcome; this does not
            start another generation request.
            {onRetry && (
              <Controls>
                <Button type="button" disabled={busy} onClick={() => invokeSafely(onRetry)}>
                  Refresh status
                </Button>
              </Controls>
            )}
          </Status>
        )}
        {(error || externalError) && <ErrorText role="alert">{error || externalError}</ErrorText>}
        {canRetryStatus && (
          <Controls>
            <Button type="button" disabled={busy} onClick={() => invokeSafely(onRetry)}>
              Retry status
            </Button>
          </Controls>
        )}
      </div>
    </Card>
  );
};

export default VideoPreviewCard;
